/**
 * Markdown → typeset physics.
 *
 * remark-math + rehype-katex handle the equations; the `code` renderer is
 * overridden so a ```plot fence becomes a live figure instead of a wall of
 * JSON. Everything else is deliberately plain — the styling lives in CSS so the
 * prose and the equations end up in the same typeface.
 */

import {
  cloneElement,
  isValidElement,
  memo,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import { PlotBlock } from "./PlotBlock.tsx";
import { AnimBlock } from "./AnimBlock.tsx";
import { indexFigures } from "./figures.ts";
import { normalizeMath } from "./normalize-math.ts";
import { Callout, readMarker } from "./Callout.tsx";

interface MarkdownProps {
  readonly content: string;
  /** False while streaming, so half-written blocks render as placeholders. */
  readonly complete?: boolean;
}

/** KaTeX throwing on a malformed fragment must not blank the whole answer. */
const KATEX_OPTIONS = {
  throwOnError: false,
  strict: false as const,
  trust: false,
  macros: {
    "\\d": "\\mathrm{d}",
    "\\vect": "\\mathbf{#1}",
    "\\unit": "\\,\\mathrm{#1}",
  },
};

export const Markdown = memo(function Markdown({
  content,
  complete = true,
}: MarkdownProps) {
  // Rewrite \( \) and \[ \] before parsing — remark-math only sees dollars.
  const source = useMemo(() => normalizeMath(content), [content]);
  const figures = useMemo(() => indexFigures(source), [source]);

  const components = useMemo<Components>(
    () => ({
      code({ className, children, ...props }) {
        const source = toText(children);
        const language = /language-(\w+)/.exec(className ?? "")?.[1];

        if (language === "plot") {
          const index = figures.numberOf(source);
          return (
            <PlotBlock
              source={source}
              {...(index !== undefined ? { index } : {})}
              complete={complete}
            />
          );
        }

        if (language === "anim") {
          const index = figures.numberOf(source);
          return (
            <AnimBlock
              source={source}
              {...(index !== undefined ? { index } : {})}
              complete={complete}
            />
          );
        }

        // Inline code has no language class and no trailing newline.
        if (!className && !source.includes("\n")) {
          return (
            <code className="inline-code" {...props}>
              {children}
            </code>
          );
        }

        return (
          <pre className="code-block">
            <code className={className} {...props}>
              {children}
            </code>
          </pre>
        );
      },

      // react-markdown wraps code fences in <pre>; ours already emits one.
      pre({ children }) {
        return <>{children}</>;
      },

      /**
       * A blockquote is either a quotation or a marked block. `[!problem]` on
       * the first line promotes it to a Problem / Solution / Step callout; the
       * marker text is stripped so the reader never sees the syntax.
       */
      blockquote({ children }) {
        const marker = readMarker(firstText(children));
        if (!marker) {
          return <blockquote className="key-idea">{children}</blockquote>;
        }

        return (
          <Callout kind={marker.kind} title={marker.title}>
            {stripMarker(children, marker.raw)}
          </Callout>
        );
      },

      table({ children }) {
        return (
          <div className="table-scroll">
            <table>{children}</table>
          </div>
        );
      },

      /**
       * Only genuinely external links open in a new tab. A footnote reference
       * is an in-page anchor, and sending it to a new tab both loses the
       * reader's place and takes them to a page fragment that isn't there.
       * The rest of the props are passed through because that is where
       * `data-footnote-ref` and the backref ids live.
       */
      a({ href, children, ...props }) {
        const external = /^https?:/i.test(href ?? "");
        return (
          <a
            href={href}
            {...props}
            {...(external
              ? { target: "_blank", rel: "noreferrer noopener" }
              : {})}
          >
            {children}
          </a>
        );
      },
    }),
    [complete, figures],
  );

  return (
    <div className="prose">
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[[rehypeKatex, KATEX_OPTIONS]]}
        components={components}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
});

function toText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(toText).join("");
  if (typeof children === "number") return String(children);
  return "";
}

/* -------------------------------------------------------------------------- */
/* Callout marker handling                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The first line of rendered text.
 *
 * react-markdown emits whitespace-only text nodes between elements, so the
 * *first* string in the tree is usually a newline rather than the content —
 * hence flattening and trimming instead of taking the first node.
 */
function firstText(children: ReactNode): string {
  return deepText(children).trimStart().split("\n")[0] ?? "";
}

/**
 * Flatten a rendered subtree to text, descending through elements.
 *
 * `toText` above deliberately stays shallow — it reads code-fence children,
 * which are plain strings. A blockquote's children are `<p>` elements, so the
 * marker lives one level down.
 */
function deepText(node: ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(deepText).join("");
  if (isElement(node)) return deepText(node.props.children);
  return "";
}

/**
 * Remove the marker from the rendered tree.
 *
 * Matches the marker text rather than counting characters, so the leading
 * whitespace nodes react-markdown inserts cannot shift the offset. Everything
 * else — including mathematics already turned into elements — passes through
 * untouched.
 */
function stripMarker(node: ReactNode, marker: string): ReactNode {
  let done = false;

  const walk = (current: ReactNode): ReactNode => {
    if (done) return current;

    if (typeof current === "string") {
      const offset = current.indexOf(marker);
      if (offset === -1) return current;

      done = true;
      // Drop the soft line break that followed the marker line.
      return (
        current.slice(0, offset) + current.slice(offset + marker.length).replace(/^\n/, "")
      );
    }

    if (Array.isArray(current)) return current.map(walk);

    if (isElement(current)) {
      return cloneElement(
        current,
        undefined,
        walk(current.props.children as ReactNode),
      );
    }

    return current;
  };

  return walk(node);
}

function isElement(
  node: ReactNode,
): node is ReactElement<{ children?: ReactNode }> {
  return isValidElement(node);
}
